/**
 * ZTile — Zephyr OS Design System
 *
 * A collapsible tile: one tap target when collapsed (title, one-line summary,
 * optional status dot and small right-hand text), its content when open. The
 * parent owns `open` so a page can run a single-open accordion. Mobile first.
 *
 * Props:
 *   title     string
 *   summary   node, shown collapsed (and as the subtitle when open)
 *   state     'ok' | 'stale' | 'down' (optional): green / amber / red dot
 *   right     small mono text on the right (optional)
 *   open      bool
 *   onToggle  () => void
 *   accent    colour for the open title and edge
 *   children  expanded content, rendered only when open
 */

const MONO = "'JetBrains Mono', monospace";
const SANS = "'Inter', sans-serif";
const DOT = { ok: '#10B981', stale: '#E8C547', down: '#FF3B30' };

export default function ZTile({ title, summary, state, right, open = false, onToggle, accent = '#6B35B8', children }) {
  return (
    <div style={{
      borderBottom: '0.5px solid rgba(255,255,255,0.06)',
      borderLeft: `2px solid ${open ? accent : 'transparent'}`,
      paddingLeft: 10,
      marginLeft: -12,
      transition: 'border-color 150ms ease',
    }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          width: '100%', minHeight: 56, display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 0', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', color: '#f0f0f5',
        }}
      >
        {state && (
          <span aria-label={state} style={{ width: 8, height: 8, borderRadius: '50%', background: DOT[state] || DOT.down, flexShrink: 0 }} />
        )}
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontFamily: MONO, fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: open ? accent : '#b0b4c0', marginBottom: 3 }}>
            {title}
          </span>
          {summary != null && summary !== '' && (
            <span style={{ display: 'block', fontFamily: SANS, fontSize: 13, lineHeight: 1.4, color: '#f0f0f5', overflowWrap: 'anywhere' }}>
              {summary}
            </span>
          )}
        </span>
        {right && <span style={{ fontFamily: MONO, fontSize: 10, color: '#6b7084', whiteSpace: 'nowrap', flexShrink: 0 }}>{right}</span>}
        <span aria-hidden style={{ fontFamily: MONO, fontSize: 12, color: '#6b7084', flexShrink: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 150ms ease' }}>›</span>
      </button>
      {open && <div style={{ padding: '0 0 18px', minWidth: 0 }}>{children}</div>}
    </div>
  );
}
